/**
 * The aggregate behind `2i` — Dashboard orçamentário.
 *
 * 🔒 **Every figure here is computed by the SERVER, and that is BR-21, not
 * laziness.** `Budget contratado (CSBs convertidos)` is a CSB count run through
 * the contract's PTAX and desconto; `Consumido até hoje` sums periods that may
 * each have been governed by a different aditivo. Converting either one in the
 * SPA would apply today's parameters to a historical month — the exact mistake
 * BR-21 exists to forbid. The screen renders these numbers; it never derives
 * them.
 *
 * ⚠️ **The endpoint is assumed — §9.** `GET /orcamento/dashboard` does not exist
 * yet. The SHAPE follows from what shot `2i` draws and from BR-21 deciding who
 * may compute it, but the path and the field names are ours until the backend
 * confirms. Every field below carries the marker.
 *
 * 🔴 **The four grouping dimensions are a REQUIREMENT, not a reading of the shot.**
 * `PROJECT-OVERVIEW-ref.md` §3.1: *"Dashboard groupings: período, provedor,
 * account ID, and origem do dado (integrado vs. manual)"*, repeated in its screen
 * map (O1) and in the handoff README itself — *"agrupamento por
 * provedor/account/tipo de lançamento"*. The same sources require a **month
 * selector**. Both were missing from the first cut of this contract; corrected
 * 2026-08-23 once Fábio pointed at the requirement.
 */
import { z } from 'zod'

import { competencia, dataIso, dinheiro, id, valorDeCatalogo } from './comum'

/**
 * One bar of the `Saldo contratual` chart.
 *
 * `previsto` is what makes the hatched bars on the right of the shot possible:
 * the same series carries measured and forecast points, distinguished by a flag
 * rather than by two arrays the screen would have to stitch together in the
 * right order.
 */
export const pontoDeConsumoSchema = z.object({
  // ⚠️ assumido — §9
  competencia,
  /** Accumulated consumption up to and including this period, already in BRL. */
  acumulado: dinheiro,
  /** The period's own consumption, so a bar can show the increment. */
  doPeriodo: dinheiro,
  /**
   * `true` for the hatched bars — the 3-month-average projection the shot labels
   * `forecast (média 3 meses)`. The screen must render these visually distinct:
   * a forecast drawn like a measurement is a number that lies.
   */
  previsto: z.boolean(),
})

export type PontoDeConsumo = z.infer<typeof pontoDeConsumoSchema>

/** A row of `Consumo por provedor / origem`. */
export const consumoAgrupadoSchema = z.object({
  /**
   * The grouping label as the server composed it — `AWS · extraído`,
   * `Azure · manual`. Composed server-side because the dashboard's `Agrupar por`
   * selector changes WHICH dimensions compose it, and the screen should not own
   * that combinatorics.
   */
  rotulo: z.string(),
  // ⚠️ assumido — §9: the raw dimensions, kept alongside the label so a future
  // drill-through does not need to parse the label back apart.
  provedor: valorDeCatalogo,
  origem: z.enum(['manual', 'integrado']),
  /**
   * 🔴 The account this consumption belongs to — the third required grouping.
   *
   * `null` for entries that belong to no AWS account: an Azure line on a
   * multi-cloud contract, or a broker credit. **Nullable rather than a
   * placeholder string**, because "sem conta AWS" is a fact about the entry and
   * `"—"` in a grouping key would collide with any account someone named that.
   *
   * One client can have several accounts (BR-16), and they are registered in
   * FinOps → Configurações (`2b`).
   */
  accountId: valorDeCatalogo.nullable(),
  /** Apelido da conta, para a tela não ter que cruzar com `2b` só para rotular. */
  apelidoDaConta: z.string().nullable(),
  valor: dinheiro,
})

export type ConsumoAgrupado = z.infer<typeof consumoAgrupadoSchema>

export const dashboardOrcamentarioSchema = z.object({
  idCliente: id,
  /** Which contract's parameters produced these figures (BR-21 traceability). */
  idContrato: id,

  /**
   * The fiscal period the figures cover, as the server resolved it — `20/06` to
   * `19/07` in the shot. Sent back rather than recomputed, because the cycle
   * start day lives on the contract and can be changed by an aditivo.
   */
  periodoInicio: dataIso,
  periodoFim: dataIso,

  /** `Budget contratado (CSBs convertidos)` — contrato vigente + aditivos. */
  budgetContratado: dinheiro,
  consumidoAteHoje: dinheiro,
  /**
   * `69,1% do contrato` in the shot. Server-sent rather than
   * `consumido / budget` in the screen: the two figures may be governed by
   * different parameter sets, so the ratio is not always the naive division.
   */
  percentualConsumido: z.number().finite(),
  saldo: dinheiro,

  /**
   * `Previsão de esgotamento` — `null` when the 3-month average does not exhaust
   * the budget within the contract term. **`null` must render as a sentence, not
   * as a date**: an empty date cell reads as "unknown", and this is "não se
   * esgota", which is the opposite of unknown.
   */
  previsaoDeEsgotamento: dataIso.nullable(),
  /** How many months the average was taken over — the shot says 3. */
  mesesDaMedia: z.number().int().positive(),

  serie: z.array(pontoDeConsumoSchema),
  /**
   * As linhas do recorte, com TODAS as dimensões em cada uma. A tela reagrupa a
   * partir daqui — é soma de BRL dentro do mesmo período, então não esbarra na
   * BR-21 (nenhuma conversão acontece no cliente).
   */
  porProvedorOrigem: z.array(consumoAgrupadoSchema),

  /**
   * 🔴 Os meses fiscais que o seletor oferece — requisito de `PROJECT-OVERVIEW`
   * §6 e do ref (O1): *"Month selector"*.
   *
   * Vem do servidor, e não é derivado de `serie`, porque `serie` é o histórico
   * DESTE recorte: derivar dela faria o seletor encolher a cada mês escolhido,
   * até sobrar um só. São duas perguntas diferentes — "que meses existem" e "o
   * que aconteceu no mês escolhido".
   */
  competenciasDisponiveis: z.array(competencia),
})

export type DashboardOrcamentario = z.infer<typeof dashboardOrcamentarioSchema>
